import { useState } from 'react';

import type { Vendor } from '@keboola/api-client/developerPortal/types';
import { Alert, Button, Modal, setSubmitError, toast, useAppForm } from '@keboola/design';

import { getApiErrorMessage } from '../../auth/errors';
import { formValidators } from '../../auth/validate';
import { useApproveVendor } from '../queries';
import { approveVendorFormSchema, suggestVendorId } from '../schemas';

const FORM_ID = 'approve-vendor';

/**
 * Approves a pending vendor, renaming it in the same step.
 *
 * - The rename is required: `_v`-prefixed temporary ids are hidden from users' selectable vendors.
 */
export function ApproveVendorModal({ vendor }: { vendor: Vendor }) {
  const [open, setOpen] = useState(false);
  const approve = useApproveVendor();

  const form = useAppForm({
    defaultValues: { tmpId: vendor.id, newId: suggestVendorId(vendor.name) },
    validators: formValidators(approveVendorFormSchema),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      try {
        await approve.mutateAsync({ vendorId: vendor.id, newId: value.newId });
        toast.success(`${vendor.name} was approved as ${value.newId}.`);
        setOpen(false);
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, 'Could not approve the vendor.'));
      }
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) form.reset();
      }}
    >
      <Modal.Trigger asChild>
        <Button variant="secondary" size="small">
          Approve
        </Button>
      </Modal.Trigger>
      <Modal.Content size="medium">
        <Modal.Header description={vendor.name}>Approve vendor</Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="tw:mb-4">
            The permanent ID becomes part of every component ID this vendor publishes, and cannot be
            changed afterwards.
          </Alert>
          <form.AppForm>
            <form.FormBody id={FORM_ID}>
              <form.AppField name="tmpId">
                {(field) => (
                  <field.TextField
                    label="Temporary ID"
                    readOnly
                    help="Assigned by the portal when the vendor registered."
                  />
                )}
              </form.AppField>
              <form.AppField name="newId">
                {(field) => (
                  <field.TextField
                    label="New vendor ID"
                    required
                    placeholder="acme-co"
                    help="Lowercase letters, digits and dashes."
                  />
                )}
              </form.AppField>
              <form.SubmitErrorAlert />
            </form.FormBody>
          </form.AppForm>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Modal.Close>
          <form.AppForm>
            <form.SubmitButton form={FORM_ID}>Approve vendor</form.SubmitButton>
          </form.AppForm>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
